import { create } from "zustand";
import { collection, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { getOrgCollectionPath } from '../constants/paths';

interface TracksState {
  tracks: { [key: string]: any };
  currentOrgId: string | null;
  isInitialized: boolean;
  unsubscribe: Unsubscribe | null;

  setTracks: (tracks: { [key: string]: any }) => void;
  queryTracks: (filter?: any) => any[];
  initTracks: (firebase: any, orgId?: string | null) => void;
  switchOrganization: (firebase: any, orgId: string) => void;
  clearTracks: () => void;
}

export const useTracksStore = create<TracksState>((set, get) => ({
  tracks: {},
  currentOrgId: null,
  isInitialized: false,
  unsubscribe: null,

  setTracks: (tracks: { [key: string]: any }) => set({ tracks }),

  queryTracks: () => Object.values(get().tracks),

  /**
   * Initialize tracks listener for the given organization
   * If no orgId provided, falls back to legacy root-level audiometa collection
   * Also handles switching between organizations
   */
  initTracks: (firebase: any, orgId?: string | null) => {
    const state = get();
    const normalizedOrgId = orgId || null;

    // If already initialized for this exact org, skip
    if (state.isInitialized && state.currentOrgId === normalizedOrgId) {
      return;
    }

    // Clean up any existing listener (important for org switching)
    if (state.unsubscribe) {
      state.unsubscribe();
    }

    // Clear existing tracks when switching orgs
    set({ tracks: {}, isInitialized: false });

    if (!firebase) {
      set({ isInitialized: true, currentOrgId: normalizedOrgId });
      return;
    }

    // Determine the collection path based on whether orgId is provided
    let audiometaPath: string;
    if (normalizedOrgId) {
      // Multi-tenant: organization-scoped collection
      audiometaPath = getOrgCollectionPath(normalizedOrgId, 'audiometa');
    } else {
      // Legacy: root-level collection (for backwards compatibility)
      audiometaPath = 'audiometa';
    }

    const audiometaRef = collection(firebase.db, audiometaPath);
    const unsubscribe = onSnapshot(audiometaRef, (snapshot: any) => {
      const tracks: { [key: string]: any } = {};
      snapshot.forEach((doc: any) => {
        tracks[doc.id] = { ...doc.data(), id: doc.id };
      });
      set({ tracks });
    });

    set({
      isInitialized: true,
      currentOrgId: normalizedOrgId,
      unsubscribe,
    });
  },

  /**
   * Switch to a different organization's tracks
   * This will unsubscribe from the current listener and subscribe to the new org
   */
  switchOrganization: (firebase: any, orgId: string) => {
    const state = get();

    // If same org, do nothing
    if (state.currentOrgId === orgId) {
      return;
    }

    // Clean up existing listener
    if (state.unsubscribe) {
      state.unsubscribe();
    }

    // Clear current tracks
    set({ tracks: {}, isInitialized: false });

    // Re-initialize with new org
    get().initTracks(firebase, orgId);
  },

  /**
   * Clear tracks and unsubscribe (for logout)
   */
  clearTracks: () => {
    const state = get();
    if (state.unsubscribe) {
      state.unsubscribe();
    }
    set({
      tracks: {},
      currentOrgId: null,
      isInitialized: false,
      unsubscribe: null,
    });
  },
}));
